/**
 * Formatting Utilities
 * Module 07 - Dashboard UI
 * 
 * Common formatting functions for displaying data
 */

import { format, formatDistanceToNow, parseISO } from 'date-fns';

/**
 * Format bytes to human-readable string
 * 
 * @param bytes - Number of bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "1.5 MB")
 * 
 * @example
 * formatBytes(1024) // "1.00 KB"
 * formatBytes(1536000) // "1.46 MB"
 * formatBytes(1073741824, 0) // "1 GB"
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Format ISO date string to readable format
 * 
 * @param dateString - ISO date string
 * @param formatString - Date format (default: 'dd MMM yyyy')
 * @returns Formatted date string
 * 
 * @example
 * formatDate('2025-01-15T10:30:00Z') // "15 Jan 2025"
 * formatDate('2025-01-15T10:30:00Z', 'dd/MM/yyyy') // "15/01/2025"
 */
export function formatDate(dateString: string, formatString: string = 'dd MMM yyyy'): string {
  try {
    return format(parseISO(dateString), formatString);
  } catch {
    return 'Invalid date';
  }
}

/**
 * Format ISO date string to relative time
 * 
 * @param dateString - ISO date string
 * @returns Relative time string (e.g., "2 hours ago")
 * 
 * @example
 * formatRelativeTime('2025-01-15T10:30:00Z') // "2 hours ago"
 */
export function formatRelativeTime(dateString: string): string {
  try {
    return formatDistanceToNow(parseISO(dateString), { addSuffix: true });
  } catch {
    return 'Unknown';
  }
}

/**
 * Format number as percentage
 * 
 * @param value - Decimal value (0-1)
 * @param decimals - Number of decimal places (default: 0)
 * @returns Percentage string
 * 
 * @example
 * formatPercentage(0.75) // "75%"
 * formatPercentage(0.3333, 2) // "33.33%"
 */
export function formatPercentage(value: number, decimals: number = 0): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Truncate string to maximum length with ellipsis
 * 
 * @param str - String to truncate
 * @param maxLength - Maximum length (default: 50)
 * @returns Truncated string
 * 
 * @example
 * truncateString('This is a very long filename.glb', 20) // "This is a very lo..."
 */
export function truncateString(str: string, maxLength: number = 50): string {
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength - 3)}...`;
}

/**
 * Format model status for display
 * 
 * @param status - Model status
 * @returns Capitalised status string
 * 
 * @example
 * formatStatus('processing') // "Processing"
 */
export function formatStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Extract filename from URL or path
 * 
 * @param urlOrPath - URL or file path
 * @returns Filename
 * 
 * @example
 * getFilename('/path/to/model.glb') // "model.glb"
 * getFilename('https://cdn.example.com/models/abc123.glb') // "abc123.glb"
 */
export function getFilename(urlOrPath: string): string {
  return urlOrPath.split('/').pop() || 'Unknown';
}

/**
 * Calculate compression ratio
 * 
 * @param originalSize - Original file size in bytes
 * @param optimisedSize - Optimised file size in bytes
 * @returns Compression percentage (0-100)
 *
 * @example
 * calculateCompressionRatio(1000000, 200000) // 80 (80% smaller)
 */
export function calculateCompressionRatio(originalSize: number, optimisedSize: number): number {
  if (originalSize === 0) return 0;
  return Math.round((1 - optimisedSize / originalSize) * 100);
}
